import { FC, useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Line,
	LineChart,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import SendIcon from "@mui/icons-material/Send";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { useQuery } from "@tanstack/react-query";
import { useUserStore } from "../../../../../stores/user.store";
import { useAppTheme } from "../../../../../utils/theme";
import { Program } from "../../../../../domain/program.interface";
import {
	AnalyticsGeneratedReport,
	AnalyticsReportDefinition,
	AnalyticsReportFeedback,
	AnalyticsReportPreview,
	analyticsService,
} from "../../../../../services/analytics.service";
import {
	PendingTurn,
	TURN_GENERIC_FAILURE,
	TURN_UNANSWERED_NOTE,
	findSessionForTurn,
	isTurnPending,
	isTurnSettled,
	isUnansweredQuestion,
	newTurnId,
	resumableTurn,
	settledTurnBubble,
	turnPollInterval,
	turnProgressLabel,
} from "./turnStatus";
import {
	buildLineChartModel,
	describeCrossing,
	describeMethod,
} from "./projection";
import {
	canSaveAsDefinition,
	classifyRunFailure,
	defaultDefinitionName,
	describeParameters,
	describeParamType,
	describeSaveFailure,
	DEFINITIONS_EMPTY,
	DEFINITIONS_PRIVACY_NOTE,
	FEEDBACK_DISCLAIMER,
	FEEDBACK_FAILED,
	FEEDBACK_PROMPT,
	FEEDBACK_RECORDED,
	initialParamValues,
	ParamFieldError,
	RunFailure,
	toRunValues,
	validateParamValues,
} from "./definitions";

interface AnalyticsAssistantTabProps {
	programs: Program[];
}

interface Bubble {
	role: "USER" | "ASSISTANT";
	content: string;
	report?: AnalyticsGeneratedReport;
	reportId?: string;
	/**
	 * SIMS-7875 — an annotation under the bubble rather than a message of its own.
	 * Used to mark a question whose turn never produced an answer, which is what
	 * the durability fix deliberately leaves behind instead of losing it.
	 */
	note?: string;
}

const PREDEFINED: Array<{ label: string; prompt: string; hint: string }> = [
	{
		label: "Enrollment Summary",
		prompt: "Generate an Enrollment Summary report.",
		hint: "One row per enrollment · lifetime totals",
	},
	{
		label: "Program Activity Summary",
		prompt: "Generate a Program Activity Summary report.",
		hint: "Enrollment + incentive + visit KPIs",
	},
	{
		label: "Visit Detail",
		prompt: "Generate a Visit Detail report.",
		hint: "One row per visit + analytes",
	},
];

// Security: report download URLs come from the analytics service (S3 presigned
// URLs). Before handing one to a navigation/embed sink (window.open / iframe
// src), allowlist the scheme so a "javascript:" or "data:" URL can never reach
// the sink and execute script (XSS). Only http(s) downloads are ever expected.
const isSafeDownloadUrl = (raw?: string): boolean => {
	if (!raw) return false;
	try {
		const u = new URL(raw, window.location.origin);
		return u.protocol === "https:" || u.protocol === "http:";
	} catch {
		return false;
	}
};

const AnalyticsAssistantTab: FC<AnalyticsAssistantTabProps> = ({
	programs,
}) => {
	const theme = useAppTheme();
	const { userInfo } = useUserStore();
	const userId = userInfo?.id ?? "";

	const c = {
		band: theme.palette.surface.main,
		border: theme.palette.outline.light,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		accent: theme.palette.primary.main,
		accentBand: theme.palette.surface.dark,
	};

	// Categorical palette for cards/charts, derived from tenant theme tokens so
	// it follows multi-tenant branding (no hardcoded literals).
	const chartPalette = [
		theme.palette.primary.main,
		theme.palette.secondary.main,
		theme.palette.accent.main,
		theme.palette.warning.main,
		theme.palette.error.main,
		theme.palette.primary.dark,
		theme.palette.secondary.dark,
		theme.palette.accent.dark,
	];
	const colorAt = (i: number): string => chartPalette[i % chartPalette.length];

	const programRefs = useMemo(
		() => programs.map((p) => ({ id: p._id, name: p.name })),
		[programs],
	);

	const [messages, setMessages] = useState<Bubble[]>([]);
	const [chatSessionId, setChatSessionId] = useState<string>("");
	const [input, setInput] = useState<string>("");
	const [sending, setSending] = useState<boolean>(false);
	const [emailTarget, setEmailTarget] = useState<string | null>(null);
	const [emailRecipients, setEmailRecipients] = useState<string>("");
	const [emailBusy, setEmailBusy] = useState<boolean>(false);
	const [toast, setToast] = useState<string>("");
	const [pdfPreviewId, setPdfPreviewId] = useState<string | null>(null);

	// SIMS-7954 — save / re-run / feedback on saved definitions.
	const [saveTarget, setSaveTarget] = useState<string | null>(null);
	const [saveName, setSaveName] = useState<string>("");
	const [saveQuestion, setSaveQuestion] = useState<string>("");
	const [saveBusy, setSaveBusy] = useState<boolean>(false);
	const [saveError, setSaveError] = useState<string>("");
	const [feedbackByReport, setFeedbackByReport] = useState<
		Record<string, AnalyticsReportFeedback>
	>({});
	const [runTarget, setRunTarget] = useState<AnalyticsReportDefinition | null>(
		null,
	);
	const [runValues, setRunValues] = useState<Record<string, string>>({});
	const [runErrors, setRunErrors] = useState<ParamFieldError[]>([]);
	const [runFailure, setRunFailure] = useState<RunFailure | null>(null);
	const [runBusy, setRunBusy] = useState<boolean>(false);

	// SIMS-7827: the server scopes history to the authenticated principal, so no
	// identity is sent. `userId` stays in the query key only to keep the cache
	// separated per signed-in user, and gates the fetch until the profile has
	// loaded.
	const chatsQuery = useQuery({
		queryKey: ["analytics-chats", userId],
		queryFn: () => analyticsService.listChats(),
		enabled: Boolean(userId),
		staleTime: 1000 * 30,
	});

	// Same contract as the chat list: definitions are scoped `{ accountId, userId }`
	// server-side, so nothing identifies the caller here either.
	const definitionsQuery = useQuery({
		queryKey: ["analytics-definitions", userId],
		queryFn: () => analyticsService.listDefinitions(),
		enabled: Boolean(userId),
		staleTime: 1000 * 30,
	});
	const definitions: AnalyticsReportDefinition[] = definitionsQuery.data ?? [];

	/**
	 * SIMS-7875 — a turn whose own response never arrived, followed by polling.
	 *
	 * Present means the server has the question and is working on it, so the
	 * composer stays disabled and the progress line keeps updating. See
	 * `turnStatus.ts` for why a failed request is not a failed turn.
	 */
	const [pendingTurn, setPendingTurn] = useState<PendingTurn | null>(null);
	const busy = sending || Boolean(pendingTurn);

	const turnQuery = useQuery({
		queryKey: [
			"analytics-turn",
			pendingTurn?.chatSessionId,
			pendingTurn?.turnId,
		],
		queryFn: async () => {
			const turn = pendingTurn;
			if (!turn) return null;
			return analyticsService.getTurn(turn.chatSessionId, turn.turnId);
		},
		enabled: Boolean(pendingTurn),
		refetchInterval: (query) =>
			turnPollInterval(
				query.state.data ?? undefined,
				pendingTurn?.startedAt ?? Date.now(),
			),
		refetchIntervalInBackground: false,
	});

	// The answer arrives here when it was the poll, not the original request, that
	// found it. Rendered exactly as an in-request answer would have been — same
	// message, same report, same inline presentation — because the server returns
	// the same shape either way.
	useEffect(() => {
		const turn = turnQuery.data;
		if (!pendingTurn || !turn || !isTurnSettled(turn)) return;
		setMessages((prev) => [
			...prev,
			{ role: "ASSISTANT", ...settledTurnBubble(turn) },
		]);
		setPendingTurn(null);
		chatsQuery.refetch();
		// `chatsQuery` is deliberately not a dependency: its identity changes on
		// every render, and clearing `pendingTurn` is what stops this from running
		// again.
	}, [turnQuery.data, pendingTurn]);

	/**
	 * The request failed — find out whether the TURN did.
	 *
	 * Asking the server beats interpreting the error: it is the only party that
	 * knows whether the question was accepted, and a gateway timeout looks like a
	 * failure while being the ordinary way a long report starts. Returns false
	 * when the server never took the turn, which is the only case that deserves an
	 * error in the chat.
	 */
	const followTurnAfterFailedRequest = async (
		turnId: string,
		message: string,
	): Promise<boolean> => {
		try {
			let sessionId = chatSessionId;
			if (!sessionId) {
				// A brand-new conversation: its id was allocated server-side and the
				// response that would have carried it is what we just lost. The turn id
				// is the handle we still have.
				const chats = await analyticsService.listChats();
				sessionId = findSessionForTurn(chats, turnId)?._id ?? "";
			}
			if (!sessionId) return false;
			const turn = await analyticsService.getTurn(sessionId, turnId);
			if (!turn?.status) return false;
			setChatSessionId(sessionId);
			if (isTurnSettled(turn)) {
				setMessages((prev) => [
					...prev,
					{ role: "ASSISTANT", ...settledTurnBubble(turn) },
				]);
				chatsQuery.refetch();
				return true;
			}
			setPendingTurn({
				chatSessionId: sessionId,
				turnId,
				message,
				startedAt: Date.now(),
			});
			return true;
		} catch {
			// A 404 here is the server saying it never saw this turn.
			return false;
		}
	};

	const send = async (text: string): Promise<void> => {
		const message = text.trim();
		if (!message || busy) return;
		// SIMS-7875: minted before sending and reused on any retry, so the server
		// can recognise a resubmission instead of asking the question twice.
		const turnId = newTurnId();
		setInput("");
		setMessages((prev) => [...prev, { role: "USER", content: message }]);
		setSending(true);
		try {
			const res = await analyticsService.sendMessage({
				message,
				chatSessionId: chatSessionId || undefined,
				programs: programRefs,
				turnId,
			});
			setChatSessionId(res.chatSessionId);
			if (isTurnPending(res)) {
				// This turn is already under way server-side; follow it rather than
				// rendering a placeholder as if it were the answer.
				setPendingTurn({
					chatSessionId: res.chatSessionId,
					turnId: res.turnId ?? turnId,
					message,
					startedAt: Date.now(),
				});
				return;
			}
			setMessages((prev) => [
				...prev,
				{
					role: "ASSISTANT",
					content: res.message,
					report: res.report,
				},
			]);
			chatsQuery.refetch();
		} catch (err) {
			const followed = await followTurnAfterFailedRequest(turnId, message);
			if (!followed) {
				const msg =
					(err as { message?: string })?.message ?? TURN_GENERIC_FAILURE;
				setMessages((prev) => [
					...prev,
					{ role: "ASSISTANT", content: `⚠️ ${msg}` },
				]);
			}
		} finally {
			setSending(false);
		}
	};

	const newChat = (): void => {
		setMessages([]);
		setChatSessionId("");
		setInput("");
		setPendingTurn(null);
	};

	const loadChat = async (id: string): Promise<void> => {
		try {
			const session = await analyticsService.getChat(id);
			setChatSessionId(session._id);
			const history = session.messages ?? [];
			setMessages(
				history.map((m, i) => ({
					role: m.role,
					content: m.content,
					reportId: m.reportId,
					// SIMS-7875: a question with nothing after it is one whose turn did
					// not finish. Saying so is the whole point of persisting it early —
					// it reads as unanswered rather than as missing.
					...(isUnansweredQuestion(history, i, session.status)
						? { note: TURN_UNANSWERED_NOTE }
						: {}),
				})),
			);
			// A conversation left mid-turn is picked up where it was, so switching
			// away and back does not abandon a report still being built.
			setPendingTurn(resumableTurn(session) ?? null);
		} catch {
			setToast("Could not load that conversation.");
		}
	};

	const deleteChat = async (id: string): Promise<void> => {
		try {
			await analyticsService.deleteChat(id);
			if (id === chatSessionId) newChat();
			chatsQuery.refetch();
			setToast("Conversation deleted.");
		} catch {
			setToast("Could not delete that conversation.");
		}
	};

	const openReport = async (
		reportId?: string,
		knownUrl?: string,
	): Promise<void> => {
		try {
			if (knownUrl) {
				if (isSafeDownloadUrl(knownUrl)) {
					window.open(knownUrl, "_blank", "noopener");
				} else {
					setToast("Could not get the download link.");
				}
				return;
			}
			if (!reportId) return;
			const { downloadUrl } =
				await analyticsService.getReportDownload(reportId);
			if (isSafeDownloadUrl(downloadUrl)) {
				window.open(downloadUrl, "_blank", "noopener");
			} else {
				setToast("Could not get the download link.");
			}
		} catch {
			setToast("Could not get the download link.");
		}
	};

	const handleSendEmail = async (): Promise<void> => {
		if (!emailTarget) return;
		const recipients = emailRecipients
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);
		if (recipients.length === 0) return;
		setEmailBusy(true);
		try {
			const res = await analyticsService.emailReport(emailTarget, recipients);
			setToast(`Emailed to ${res.sent} recipient(s).`);
			setEmailTarget(null);
			setEmailRecipients("");
		} catch {
			setToast("Email failed to send.");
		} finally {
			setEmailBusy(false);
		}
	};

	const openSaveDialog = (report: AnalyticsGeneratedReport): void => {
		setSaveTarget(report.id);
		setSaveName(defaultDefinitionName(report));
		setSaveQuestion("");
		setSaveError("");
	};

	const handleSaveDefinition = async (): Promise<void> => {
		if (!saveTarget) return;
		const name = saveName.trim();
		if (!name) {
			setSaveError("A name is required.");
			return;
		}
		setSaveBusy(true);
		setSaveError("");
		try {
			await analyticsService.saveDefinition(saveTarget, {
				name,
				question: saveQuestion.trim() || undefined,
			});
			setSaveTarget(null);
			definitionsQuery.refetch();
			setToast("Saved. Re-run it any time from Saved reports.");
		} catch (err) {
			// The server's own words. The identifying-field refusal names the field
			// and says what to do instead, and a generic toast would throw that away.
			setSaveError(describeSaveFailure(err));
		} finally {
			setSaveBusy(false);
		}
	};

	/**
	 * Record a thumbs up/down.
	 *
	 * A signal only — see FEEDBACK_DISCLAIMER. Nothing here publishes, and the
	 * control's copy must not suggest it does.
	 */
	const handleFeedback = async (
		reportId: string,
		feedback: AnalyticsReportFeedback,
	): Promise<void> => {
		try {
			await analyticsService.rateReport(reportId, feedback);
			setFeedbackByReport((prev) => ({ ...prev, [reportId]: feedback }));
			setToast(FEEDBACK_RECORDED);
		} catch {
			setToast(FEEDBACK_FAILED);
		}
	};

	const openRunDialog = (definition: AnalyticsReportDefinition): void => {
		setRunTarget(definition);
		setRunValues(initialParamValues(definition.parameters));
		setRunErrors([]);
		setRunFailure(null);
	};

	const handleRunDefinition = async (): Promise<void> => {
		if (!runTarget) return;
		const fieldErrors = validateParamValues(runTarget.parameters, runValues);
		setRunErrors(fieldErrors);
		if (fieldErrors.length > 0) {
			setRunFailure(null);
			return;
		}
		setRunBusy(true);
		setRunFailure(null);
		try {
			const report = await analyticsService.runDefinition(
				runTarget._id,
				toRunValues(runTarget.parameters, runValues),
			);
			// Rendered through the same bubble as a chat-generated report, so a
			// re-run is not a second presentation path that can drift from the first.
			setMessages((prev) => [
				...prev,
				{
					role: "ASSISTANT",
					content: `Re-ran your saved report “${runTarget.name}”.`,
					report,
				},
			]);
			setRunTarget(null);
			definitionsQuery.refetch();
		} catch (err) {
			setRunFailure(classifyRunFailure(err, runTarget.name));
		} finally {
			setRunBusy(false);
		}
	};

	const handleDeleteDefinition = async (
		definitionId: string,
	): Promise<void> => {
		try {
			await analyticsService.deleteDefinition(definitionId);
			definitionsQuery.refetch();
			setToast("Saved report removed.");
		} catch {
			setToast("Could not remove that saved report.");
		}
	};

	// The agent chooses how the result is shown inline: a single stat card, a
	// grid of KPI cards, a pie/bar chart, or a compact table. The cards/charts
	// use a tinted, colored surface so they stand out from the chat. Full data
	// is always in the download.
	const ResultPreview: FC<{ preview?: AnalyticsReportPreview }> = ({
		preview,
	}) => {
		if (!preview) return null;

		if (preview.kind === "SCALAR") {
			const accent = colorAt(0);
			return (
				<Box
					sx={{
						mt: 1.25,
						p: 2,
						borderRadius: 2,
						borderLeft: `4px solid ${accent}`,
						border: `1px solid ${alpha(accent, 0.35)}`,
						bgcolor: alpha(accent, 0.1),
						display: "inline-flex",
						flexDirection: "column",
						minWidth: 180,
					}}
				>
					<Typography
						sx={{
							fontSize: 12,
							color: c.muted,
							textTransform: "uppercase",
							letterSpacing: 0.4,
						}}
					>
						{preview.valueLabel}
					</Typography>
					<Typography
						sx={{
							fontSize: 38,
							fontWeight: 800,
							color: accent,
							lineHeight: 1.1,
						}}
					>
						{String(preview.value)}
					</Typography>
				</Box>
			);
		}

		if (preview.kind === "KPI_GRID") {
			return (
				<Box
					sx={{
						mt: 1.25,
						display: "grid",
						gridTemplateColumns: {
							xs: "repeat(2, 1fr)",
							sm: "repeat(auto-fill, minmax(140px, 1fr))",
						},
						gap: 1,
					}}
				>
					{(preview.metrics ?? []).map((m, i) => {
						const accent = colorAt(i);
						return (
							<Box
								key={m.label}
								sx={{
									p: 1.25,
									borderRadius: 2,
									borderLeft: `4px solid ${accent}`,
									border: `1px solid ${alpha(accent, 0.35)}`,
									bgcolor: alpha(accent, 0.1),
									minWidth: 0,
								}}
							>
								<Typography
									sx={{ fontSize: 24, fontWeight: 800, color: accent }}
								>
									{String(m.value)}
								</Typography>
								<Typography
									sx={{
										fontSize: 12,
										color: c.muted,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{m.label}
								</Typography>
							</Box>
						);
					})}
				</Box>
			);
		}

		// A trend, single- or multi-series. Kept separate from PIE/BAR because the
		// x-axis is ordered: the point order IS the timeline, so it must not be
		// re-sorted or de-duplicated here.
		if (preview.kind === "LINE") {
			// SIMS-7851: `projection` is a sibling of `metrics`, never merged into
			// it, so the measured series is untouched by the projected tail. See
			// ./projection.ts for the merge rules and why they live there.
			const { data, seriesKeys, projectedPoints, computed, refusal, isMulti } =
				buildLineChartModel(preview);

			return (
				<Box
					sx={{
						mt: 1.25,
						p: 1.5,
						borderRadius: 2,
						border: `1px solid ${c.border}`,
						bgcolor: alpha(c.accent, 0.06),
					}}
				>
					<Typography
						sx={{ fontSize: 12, color: c.muted, mb: 0.5, fontWeight: 600 }}
					>
						{preview.title}
					</Typography>
					<Box sx={{ width: "100%", height: 240 }}>
						<ResponsiveContainer>
							<LineChart
								data={data}
								margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
							>
								<CartesianGrid strokeDasharray="3 3" stroke={c.border} />
								<XAxis dataKey="name" />
								{/* Anchored at zero so a small change cannot be exaggerated
								    into a dramatic slope. */}
								<YAxis allowDecimals={false} domain={[0, "auto"]} />
								<Tooltip />
								{seriesKeys.map((key, i) => (
									<Line
										key={key}
										type="monotone"
										dataKey={key}
										stroke={colorAt(i)}
										strokeWidth={2}
										dot={{ r: 3 }}
										// Must NOT bridge the projected rows, or the solid
										// "measured" line would run through them.
										connectNulls={false}
									/>
								))}
								{projectedPoints.length > 0 && (
									<Line
										key="__projected"
										// Straight, because the fit IS a straight line. A monotone
										// curve would imply a shape the method never produced.
										type="linear"
										dataKey="projected"
										name="Projected"
										stroke={colorAt(0)}
										strokeOpacity={0.7}
										strokeWidth={2}
										strokeDasharray="5 4"
										// Hollow markers: the difference survives greyscale and
										// colour-blindness, where a hue shift alone would not.
										dot={{
											r: 3,
											fill: c.band,
											stroke: colorAt(0),
											strokeWidth: 1.5,
										}}
										connectNulls={false}
									/>
								)}
							</LineChart>
						</ResponsiveContainer>
					</Box>
					{isMulti && (
						<Stack
							direction="row"
							flexWrap="wrap"
							sx={{ gap: 1, mt: 1 }}
							aria-label="chart legend"
						>
							{seriesKeys.map((key, i) => (
								<Stack
									key={key}
									direction="row"
									alignItems="center"
									spacing={0.75}
								>
									<Box
										sx={{
											width: 12,
											height: 12,
											borderRadius: 0.5,
											bgcolor: colorAt(i),
										}}
									/>
									<Typography sx={{ fontSize: 12, color: c.ink }}>
										{key}
									</Typography>
								</Stack>
							))}
						</Stack>
					)}

					{projectedPoints.length > 0 && (
						<Stack
							direction="row"
							flexWrap="wrap"
							sx={{ gap: 1.5, mt: 1 }}
							aria-label="projection legend"
						>
							<Stack direction="row" alignItems="center" spacing={0.75}>
								<Box
									sx={{ width: 16, height: 2, bgcolor: colorAt(0) }}
									aria-hidden
								/>
								<Typography sx={{ fontSize: 12, color: c.ink }}>
									Measured
								</Typography>
							</Stack>
							<Stack direction="row" alignItems="center" spacing={0.75}>
								<Box
									sx={{
										width: 16,
										borderTop: `2px dashed ${alpha(colorAt(0), 0.7)}`,
									}}
									aria-hidden
								/>
								<Typography sx={{ fontSize: 12, color: c.ink }}>
									Projected — not measured
								</Typography>
							</Stack>
						</Stack>
					)}

					{/* The method and its limits travel with the numbers. A reader must
					    never have to infer that the dashed tail is an extrapolation. */}
					{computed && (
						<Box sx={{ mt: 1 }}>
							<Typography sx={{ fontSize: 11, color: c.muted }}>
								{describeMethod(computed)}
							</Typography>
							{computed.crossing && (
								<Typography
									sx={{
										fontSize: 11,
										color: c.ink,
										mt: 0.5,
										fontWeight: 600,
									}}
								>
									{describeCrossing(computed)}
								</Typography>
							)}
							{computed.caveats.map((caveat) => (
								<Typography
									key={caveat}
									sx={{ fontSize: 11, color: c.muted, mt: 0.25 }}
								>
									{`• ${caveat}`}
								</Typography>
							))}
						</Box>
					)}

					{/* A refusal is an answer. Showing it stops the reader assuming a
					    projection was simply forgotten. */}
					{refusal && (
						<Typography sx={{ fontSize: 11, color: c.muted, mt: 1 }}>
							{`No projection: ${refusal.reason}`}
						</Typography>
					)}
				</Box>
			);
		}

		if (preview.kind === "PIE" || preview.kind === "BAR") {
			const data = (preview.metrics ?? []).map((m) => ({
				name: m.label,
				value: typeof m.value === "number" ? m.value : Number(m.value) || 0,
			}));
			return (
				<Box
					sx={{
						mt: 1.25,
						p: 1.5,
						borderRadius: 2,
						border: `1px solid ${c.border}`,
						bgcolor: alpha(c.accent, 0.06),
					}}
				>
					<Box sx={{ width: "100%", height: 240 }}>
						<ResponsiveContainer width="100%" height="100%">
							{preview.kind === "PIE" ? (
								<PieChart>
									<Tooltip />
									<Pie
										data={data}
										dataKey="value"
										nameKey="name"
										innerRadius={50}
										outerRadius={90}
										paddingAngle={1}
									>
										{data.map((entry, i) => (
											<Cell key={entry.name} fill={colorAt(i)} />
										))}
									</Pie>
								</PieChart>
							) : (
								<BarChart
									data={data}
									margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
								>
									<CartesianGrid strokeDasharray="3 3" vertical={false} />
									<XAxis dataKey="name" />
									<YAxis allowDecimals={false} />
									<Tooltip />
									<Bar dataKey="value">
										{data.map((entry, i) => (
											<Cell key={entry.name} fill={colorAt(i)} />
										))}
									</Bar>
								</BarChart>
							)}
						</ResponsiveContainer>
					</Box>
					{/* Accessible / always-rendered legend (theme-colored swatches). */}
					<Stack
						direction="row"
						flexWrap="wrap"
						sx={{ gap: 1, mt: 1 }}
						aria-label="chart legend"
					>
						{data.map((entry, i) => (
							<Stack
								key={entry.name}
								direction="row"
								alignItems="center"
								spacing={0.75}
							>
								<Box
									sx={{
										width: 12,
										height: 12,
										borderRadius: 0.5,
										bgcolor: colorAt(i),
									}}
								/>
								<Typography sx={{ fontSize: 12, color: c.ink }}>
									{entry.name}: <b>{entry.value}</b>
								</Typography>
							</Stack>
						))}
					</Stack>
				</Box>
			);
		}

		const columns = preview.columns ?? [];
		const rows = preview.rows ?? [];
		return (
			<Box
				sx={{
					mt: 1.25,
					border: `1px solid ${c.border}`,
					borderRadius: 2,
					overflowX: "auto",
				}}
			>
				<Box
					component="table"
					sx={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}
				>
					<Box component="thead">
						<Box component="tr">
							{columns.map((col) => (
								<Box
									component="th"
									key={col}
									sx={{
										textAlign: "left",
										p: 1,
										bgcolor: c.band,
										color: c.ink,
										fontWeight: 700,
										whiteSpace: "nowrap",
										borderBottom: `1px solid ${c.border}`,
									}}
								>
									{col}
								</Box>
							))}
						</Box>
					</Box>
					<Box component="tbody">
						{rows.map((r, i) => (
							<Box component="tr" key={i}>
								{columns.map((col) => (
									<Box
										component="td"
										key={col}
										sx={{
											p: 1,
											color: c.ink,
											whiteSpace: "nowrap",
											borderBottom: `1px solid ${c.border}`,
										}}
									>
										{r[col]}
									</Box>
								))}
							</Box>
						))}
					</Box>
				</Box>
				{preview.totalRows && preview.totalRows > rows.length ? (
					<Typography sx={{ fontSize: 12, color: c.muted, p: 1 }}>
						Showing {rows.length} of {preview.totalRows} rows — full data in the
						download.
					</Typography>
				) : null}
			</Box>
		);
	};

	const ReportActions: FC<{ bubble: Bubble }> = ({ bubble }) => {
		const report = bubble.report;
		const reportId = report?.id ?? bubble.reportId;
		if (!reportId) return null;
		// isSafeDownloadUrl also gates the <iframe src> below: an unsafe scheme
		// (javascript:/data:) never renders a preview.
		const isPdf =
			report?.format === "PDF" && isSafeDownloadUrl(report?.downloadUrl);
		const previewOpen = pdfPreviewId === reportId;
		return (
			<>
				<Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
					<Button
						size="small"
						variant="contained"
						startIcon={<FileDownloadOutlinedIcon />}
						onClick={() => openReport(reportId, report?.downloadUrl)}
					>
						Download
					</Button>
					<Button
						size="small"
						variant="outlined"
						startIcon={<MailOutlineIcon />}
						onClick={() => setEmailTarget(reportId)}
					>
						Email
					</Button>
					{isPdf ? (
						<Button
							size="small"
							variant="text"
							startIcon={<PictureAsPdfOutlinedIcon />}
							onClick={() => setPdfPreviewId(previewOpen ? null : reportId)}
						>
							{previewOpen ? "Hide document" : "Preview document"}
						</Button>
					) : null}
					{/* Only a CUSTOM report has a pipeline shape to save; see
					    canSaveAsDefinition for why the native types are excluded. */}
					{report && canSaveAsDefinition(report) ? (
						<Button
							size="small"
							variant="text"
							startIcon={<BookmarkAddOutlinedIcon />}
							onClick={() => openSaveDialog(report)}
						>
							Save as reusable report
						</Button>
					) : null}
				</Stack>
				{isPdf && previewOpen ? (
					<Box
						sx={{
							mt: 1.25,
							border: `1px solid ${c.border}`,
							borderRadius: 2,
							overflow: "hidden",
							bgcolor: c.band,
						}}
					>
						<iframe
							title={report?.fileName}
							src={report?.downloadUrl}
							style={{ width: "100%", height: 420, border: "none" }}
						/>
					</Box>
				) : null}
				{/* A rating is a signal, not a publication. The caption below says so
				    in the control itself — a thumbs-up that looked like it taught the
				    assistant something would be a promise the backend never makes. */}
				<Stack
					direction="row"
					alignItems="center"
					flexWrap="wrap"
					sx={{ gap: 0.25, mt: 1 }}
				>
					<Typography sx={{ fontSize: 12, color: c.muted, mr: 0.5 }}>
						{FEEDBACK_PROMPT}
					</Typography>
					<IconButton
						size="small"
						aria-label="This report was useful"
						onClick={() => handleFeedback(reportId, "UP")}
						sx={{
							color: feedbackByReport[reportId] === "UP" ? c.accent : c.muted,
						}}
					>
						{feedbackByReport[reportId] === "UP" ? (
							<ThumbUpIcon fontSize="small" />
						) : (
							<ThumbUpOutlinedIcon fontSize="small" />
						)}
					</IconButton>
					<IconButton
						size="small"
						aria-label="This report was not useful"
						onClick={() => handleFeedback(reportId, "DOWN")}
						sx={{
							color: feedbackByReport[reportId] === "DOWN" ? c.accent : c.muted,
						}}
					>
						{feedbackByReport[reportId] === "DOWN" ? (
							<ThumbDownIcon fontSize="small" />
						) : (
							<ThumbDownOutlinedIcon fontSize="small" />
						)}
					</IconButton>
				</Stack>
				<Typography sx={{ fontSize: 11, color: c.muted }}>
					{FEEDBACK_DISCLAIMER}
				</Typography>
			</>
		);
	};

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 300px" },
				gap: 3,
				minWidth: 0,
			}}
		>
			{/* Main: predefined prompts + chat */}
			<Stack spacing={2} sx={{ minWidth: 0 }}>
				<Paper
					variant="outlined"
					sx={{ borderColor: c.border, borderRadius: 3, p: 2.5 }}
				>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						sx={{ mb: 1.5 }}
					>
						<Typography sx={{ fontSize: 13, fontWeight: 700, color: c.muted }}>
							PREDEFINED REPORTS
						</Typography>
						<Button
							size="small"
							startIcon={<AddCommentOutlinedIcon />}
							onClick={newChat}
						>
							New chat
						</Button>
					</Stack>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								sm: "repeat(3, minmax(0,1fr))",
							},
							gap: 1.5,
						}}
					>
						{PREDEFINED.map((p) => (
							<Paper
								key={p.label}
								variant="outlined"
								onClick={() => send(p.prompt)}
								sx={{
									borderColor: c.border,
									borderRadius: 2,
									p: 1.5,
									cursor: busy ? "default" : "pointer",
									opacity: busy ? 0.6 : 1,
									"&:hover": { bgcolor: c.band },
								}}
							>
								<Typography
									sx={{ fontSize: 15, fontWeight: 600, color: c.ink }}
								>
									{p.label}
								</Typography>
								<Typography sx={{ fontSize: 12, color: c.muted, mt: 0.25 }}>
									{p.hint}
								</Typography>
							</Paper>
						))}
					</Box>
				</Paper>

				<Paper
					variant="outlined"
					sx={{
						borderColor: c.border,
						borderRadius: 3,
						display: "flex",
						flexDirection: "column",
						minHeight: 420,
					}}
				>
					<Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
						{messages.length === 0 ? (
							<Stack
								alignItems="center"
								justifyContent="center"
								spacing={1}
								sx={{
									height: "100%",
									color: c.muted,
									textAlign: "center",
									py: 6,
								}}
							>
								<AutoAwesomeIcon sx={{ fontSize: 40, color: c.accent }} />
								<Typography sx={{ fontSize: 16 }}>
									Ask for a report, or pick a predefined one above.
								</Typography>
								<Typography sx={{ fontSize: 13 }}>
									e.g. “Enrollment summary for the Maine CM program, active
									members”.
								</Typography>
							</Stack>
						) : (
							<Stack spacing={1.5}>
								{messages.map((m, i) => (
									<Box
										key={i}
										sx={{
											alignSelf: m.role === "USER" ? "flex-end" : "flex-start",
											maxWidth: "85%",
											bgcolor: m.role === "USER" ? c.accentBand : c.band,
											border: `1px solid ${c.border}`,
											borderRadius: 2,
											px: 1.75,
											py: 1.25,
										}}
									>
										<Typography
											sx={{
												fontSize: 15,
												color: c.ink,
												whiteSpace: "pre-wrap",
											}}
										>
											{m.content}
										</Typography>
										<ResultPreview preview={m.report?.preview} />
										<ReportActions bubble={m} />
										{m.note && (
											<Typography
												sx={{
													fontSize: 13,
													fontStyle: "italic",
													color: c.muted,
													mt: 0.75,
												}}
											>
												{m.note}
											</Typography>
										)}
									</Box>
								))}
								{busy && (
									<Stack
										direction="row"
										spacing={1}
										alignItems="center"
										sx={{ color: c.muted }}
									>
										<CircularProgress size={14} />
										{/* SIMS-7875: the phase, so a long turn reads as progress
										    rather than as a hang. */}
										<Typography sx={{ fontSize: 14 }}>
											{turnProgressLabel(
												turnQuery.data ?? undefined,
												Boolean(pendingTurn),
											)}
										</Typography>
									</Stack>
								)}
							</Stack>
						)}
					</Box>
					<Divider />
					<Stack direction="row" spacing={1} sx={{ p: 1.5 }}>
						<TextField
							fullWidth
							size="small"
							placeholder="Describe the report you need…"
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									send(input);
								}
							}}
							disabled={busy}
						/>
						<IconButton
							color="primary"
							onClick={() => send(input)}
							disabled={busy || !input.trim()}
							aria-label="Send"
						>
							<SendIcon />
						</IconButton>
					</Stack>
				</Paper>
			</Stack>

			{/* Saved reports + chat history */}
			<Stack spacing={3} sx={{ minWidth: 0 }}>
				<Paper
					variant="outlined"
					sx={{ borderColor: c.border, borderRadius: 3, p: 2, minWidth: 0 }}
				>
					<Typography sx={{ fontSize: 13, fontWeight: 700, color: c.muted }}>
						SAVED REPORTS
					</Typography>
					{/* Definitions are per-user by design (SIMS-7811): a name is
					    user-authored free text, so account-wide visibility would put one
					    user's wording into another's. Stating it is part of the design,
					    not a disclaimer. */}
					<Typography sx={{ fontSize: 11, color: c.muted, mt: 0.25, mb: 1 }}>
						{DEFINITIONS_PRIVACY_NOTE}
					</Typography>
					{definitionsQuery.isLoading ? (
						<CircularProgress size={18} />
					) : definitions.length === 0 ? (
						<Typography sx={{ fontSize: 13, color: c.muted }}>
							{DEFINITIONS_EMPTY}
						</Typography>
					) : (
						<Stack spacing={1}>
							{definitions.map((definition) => (
								<Box
									key={definition._id}
									sx={{
										display: "flex",
										alignItems: "flex-start",
										gap: 0.5,
										borderRadius: 1.5,
										p: 1,
										border: `1px solid ${c.border}`,
									}}
								>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										{/* A definition name is untrusted free text. Rendered as
										    a text child so it can never be read as markup. */}
										<Typography
											sx={{
												fontSize: 14,
												color: c.ink,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{definition.name}
										</Typography>
										<Typography sx={{ fontSize: 11, color: c.muted }}>
											{describeParameters(definition)}
										</Typography>
									</Box>
									<IconButton
										size="small"
										aria-label={`Run saved report ${definition.name}`}
										onClick={() => openRunDialog(definition)}
									>
										<PlayArrowOutlinedIcon fontSize="small" />
									</IconButton>
									<IconButton
										size="small"
										aria-label={`Delete saved report ${definition.name}`}
										onClick={() => handleDeleteDefinition(definition._id)}
									>
										<DeleteOutlineIcon fontSize="small" />
									</IconButton>
								</Box>
							))}
						</Stack>
					)}
				</Paper>

				<Paper
					variant="outlined"
					sx={{ borderColor: c.border, borderRadius: 3, p: 2, minWidth: 0 }}
				>
					<Typography
						sx={{ fontSize: 13, fontWeight: 700, color: c.muted, mb: 1 }}
					>
						CHAT HISTORY
					</Typography>
					{chatsQuery.isLoading ? (
						<CircularProgress size={18} />
					) : (chatsQuery.data ?? []).length === 0 ? (
						<Typography sx={{ fontSize: 13, color: c.muted }}>
							No conversations yet.
						</Typography>
					) : (
						<Stack spacing={1}>
							{(chatsQuery.data ?? []).map((s) => (
								<Box
									key={s._id}
									sx={{
										display: "flex",
										alignItems: "flex-start",
										gap: 0.5,
										borderRadius: 1.5,
										p: 1,
										border: `1px solid ${c.border}`,
										bgcolor:
											s._id === chatSessionId ? c.accentBand : "transparent",
										"&:hover": { bgcolor: c.band },
									}}
								>
									<Box
										onClick={() => loadChat(s._id)}
										sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
									>
										<Typography
											sx={{
												fontSize: 14,
												color: c.ink,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{s.title || "Untitled"}
										</Typography>
										<Chip
											size="small"
											label={s.status}
											sx={{ height: 18, fontSize: 10, mt: 0.5 }}
										/>
									</Box>
									<IconButton
										size="small"
										aria-label="Delete conversation"
										onClick={(e) => {
											e.stopPropagation();
											deleteChat(s._id);
										}}
									>
										<DeleteOutlineIcon fontSize="small" />
									</IconButton>
								</Box>
							))}
						</Stack>
					)}
				</Paper>
			</Stack>

			{/* Email dialog */}
			<Dialog open={Boolean(emailTarget)} onClose={() => setEmailTarget(null)}>
				<DialogTitle>Email report</DialogTitle>
				<DialogContent sx={{ minWidth: 360 }}>
					<Typography sx={{ fontSize: 14, color: c.muted, mb: 1.5 }}>
						Recipients (comma-separated). A short-lived download link is
						included.
					</Typography>
					<TextField
						fullWidth
						size="small"
						placeholder="name@example.com, other@example.com"
						value={emailRecipients}
						onChange={(e) => setEmailRecipients(e.target.value)}
						autoFocus
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEmailTarget(null)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleSendEmail}
						disabled={emailBusy || !emailRecipients.trim()}
					>
						{emailBusy ? "Sending…" : "Send"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Save dialog */}
			<Dialog
				open={Boolean(saveTarget)}
				onClose={() => setSaveTarget(null)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Save as a reusable report</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: 14, color: c.muted, mb: 1.5 }}>
						The question is saved, never the answer. Re-running it queries live
						data, so the numbers will be current — not the ones above. Only you
						can see it.
					</Typography>
					<TextField
						fullWidth
						size="small"
						label="Name"
						value={saveName}
						onChange={(e) => setSaveName(e.target.value)}
						inputProps={{ maxLength: 120 }}
						autoFocus
						sx={{ mb: 1.5 }}
					/>
					<TextField
						fullWidth
						size="small"
						label="What does it answer? (optional)"
						placeholder="e.g. How many members completed a visit each month?"
						value={saveQuestion}
						onChange={(e) => setSaveQuestion(e.target.value)}
						inputProps={{ maxLength: 500 }}
						multiline
						minRows={2}
					/>
					{saveError ? (
						<Box
							sx={{
								mt: 1.5,
								p: 1.25,
								borderRadius: 2,
								border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
								bgcolor: alpha(theme.palette.error.main, 0.08),
							}}
						>
							<Typography sx={{ fontSize: 13, color: c.ink }}>
								{saveError}
							</Typography>
						</Box>
					) : null}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSaveTarget(null)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleSaveDefinition}
						disabled={saveBusy || !saveName.trim()}
					>
						{saveBusy ? "Saving…" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Re-run dialog */}
			<Dialog
				open={Boolean(runTarget)}
				onClose={() => setRunTarget(null)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Run saved report</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: 14, color: c.ink, fontWeight: 600 }}>
						{runTarget?.name}
					</Typography>
					{runTarget?.question ? (
						<Typography sx={{ fontSize: 13, color: c.muted, mt: 0.25 }}>
							{runTarget.question}
						</Typography>
					) : null}
					{(runTarget?.parameters ?? []).length === 0 ? (
						<Typography sx={{ fontSize: 13, color: c.muted, mt: 1.5 }}>
							This report takes no parameters.
						</Typography>
					) : (
						<Stack spacing={1.5} sx={{ mt: 2 }}>
							{(runTarget?.parameters ?? []).map((parameter) => {
								const fieldError = runErrors.find(
									(e) => e.name === parameter.name,
								);
								return (
									<TextField
										key={parameter.name}
										fullWidth
										size="small"
										label={parameter.label}
										value={runValues[parameter.name] ?? ""}
										onChange={(e) =>
											setRunValues((prev) => ({
												...prev,
												[parameter.name]: e.target.value,
											}))
										}
										error={Boolean(fieldError)}
										helperText={
											fieldError?.message ?? describeParamType(parameter)
										}
									/>
								);
							})}
						</Stack>
					)}
					{/* A re-run failure is an expected outcome with a specific cause, so
					    it is explained here rather than flattened into a toast. */}
					{runFailure ? (
						<Box
							sx={{
								mt: 2,
								p: 1.25,
								borderRadius: 2,
								border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
								bgcolor: alpha(theme.palette.error.main, 0.08),
							}}
						>
							<Typography sx={{ fontSize: 13, color: c.ink, fontWeight: 600 }}>
								{runFailure.heading}
							</Typography>
							<Typography sx={{ fontSize: 13, color: c.ink, mt: 0.5 }}>
								{runFailure.detail}
							</Typography>
							{runFailure.explanation ? (
								<Typography sx={{ fontSize: 12, color: c.muted, mt: 0.75 }}>
									{runFailure.explanation}
								</Typography>
							) : null}
						</Box>
					) : null}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRunTarget(null)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleRunDefinition}
						disabled={runBusy}
					>
						{runBusy ? "Running…" : "Run"}
					</Button>
				</DialogActions>
			</Dialog>

			{toast && (
				<Box
					onClick={() => setToast("")}
					sx={{
						position: "fixed",
						bottom: 24,
						left: "50%",
						transform: "translateX(-50%)",
						bgcolor: c.ink,
						color: theme.palette.surface.light,
						px: 2,
						py: 1,
						borderRadius: 2,
						fontSize: 14,
						cursor: "pointer",
						zIndex: 1400,
					}}
				>
					{toast}
				</Box>
			)}
		</Box>
	);
};

export default AnalyticsAssistantTab;
